import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, CheckCircle2, XCircle, Loader2, ChevronLeft } from 'lucide-react';
import { Button } from '@shared/components/ui/button';
import { useTenant } from '@core/tenant';
import BookingHeader from '@features/booking/components/BookingHeader';
import { BookingChrome } from '@features/booking/layouts';
import BookingFooter from '@features/booking/components/BookingFooter';
import { unsubscribeUser } from '../api/unsubscribe';

type PageState = 'idle' | 'loading' | 'success' | 'error';

const UnsubscribePage = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('booking');
  const { restaurant } = useTenant();

  const [shouldUnsubscribe, setShouldUnsubscribe] = useState(false);
  const [pageState, setPageState] = useState<PageState>('idle');

  const handleConfirm = async () => {
    if (!shouldUnsubscribe) {
      navigate('/');
      return;
    }

    if (!token || !restaurant) return;

    setPageState('loading');
    try {
      await unsubscribeUser(token, restaurant.id);
      setPageState('success');
    } catch {
      setPageState('error');
    }
  };

  const renderNoToken = () => (
    <>
      <div className="flex flex-col items-center pt-4">
        <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-6">
          <XCircle className="text-red-600" size={32} />
        </div>
        <h1 className="text-3xl font-display mb-2 text-center">
          {t('UNSUBSCRIBE_INVALID_TITLE')}
        </h1>
        <p className="text-foreground/50 text-sm mb-8 text-center">
          {t('UNSUBSCRIBE_INVALID_SUBTITLE')}
        </p>
      </div>
      <div className="w-full max-w-sm px-4">
        <Button
          className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md"
          onClick={() => navigate('/')}
        >
          {t('UNSUBSCRIBE_BACK_TO_HOME')}
        </Button>
      </div>
    </>
  );

  const renderSuccess = () => (
    <>
      <div className="flex flex-col items-center pt-4">
        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-6">
          <CheckCircle2 className="text-green-600" size={32} />
        </div>
        <h1 className="text-3xl font-display mb-2 text-center">
          {t('UNSUBSCRIBE_SUCCESS_TITLE')}
        </h1>
        <p className="text-foreground/50 text-sm mb-8 text-center">
          {t('UNSUBSCRIBE_SUCCESS_SUBTITLE')}
        </p>
      </div>
      <div className="w-full max-w-sm px-4">
        <Button
          className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md"
          onClick={() => navigate('/')}
        >
          {t('UNSUBSCRIBE_BACK_TO_HOME')}
        </Button>
      </div>
    </>
  );

  const renderForm = () => (
    <>
      <div className="flex flex-col items-center pt-4">
        <div className="w-16 h-16 rounded-full bg-brand/10 flex items-center justify-center mb-6">
          <Mail className="text-brand" size={32} />
        </div>
        <h1 className="text-3xl font-display mb-2 text-center">
          {t('UNSUBSCRIBE_TITLE')}
        </h1>
        <p className="text-foreground/50 text-sm mb-4 text-center px-4">
          {t('UNSUBSCRIBE_INFO')}
        </p>
        <p className="text-foreground/70 text-sm mb-8 text-center px-4">
          {t('UNSUBSCRIBE_QUESTION')}
        </p>
      </div>

      <div className="w-full max-w-sm px-4 space-y-3">
        <label
          className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-colors ${
            !shouldUnsubscribe
              ? 'border-brand bg-brand/5'
              : 'border-border hover:border-foreground/20'
          }`}
        >
          <input
            type="radio"
            name="unsubscribe"
            checked={!shouldUnsubscribe}
            onChange={() => setShouldUnsubscribe(false)}
            className="w-4 h-4 accent-brand"
          />
          <span className="text-sm text-foreground">
            {t('UNSUBSCRIBE_OPTION_STAY')}
          </span>
        </label>

        <label
          className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-colors ${
            shouldUnsubscribe
              ? 'border-brand bg-brand/5'
              : 'border-border hover:border-foreground/20'
          }`}
        >
          <input
            type="radio"
            name="unsubscribe"
            checked={shouldUnsubscribe}
            onChange={() => setShouldUnsubscribe(true)}
            className="w-4 h-4 accent-brand"
          />
          <span className="text-sm text-foreground">
            {t('UNSUBSCRIBE_OPTION_LEAVE')}
          </span>
        </label>
      </div>

      {pageState === 'error' && (
        <p className="text-red-500 text-sm mt-4 text-center px-4">
          {t('UNSUBSCRIBE_ERROR')}
        </p>
      )}

      <div className="w-full max-w-sm px-4 mt-8">
        <Button
          className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md"
          onClick={handleConfirm}
          disabled={pageState === 'loading'}
        >
          {pageState === 'loading' ? (
            <>
              <Loader2 size={18} className="mr-2 animate-spin" />
              {t('UNSUBSCRIBE_CONFIRMING')}
            </>
          ) : (
            t('UNSUBSCRIBE_CONFIRM')
          )}
        </Button>
      </div>
    </>
  );

  return (
    <BookingChrome flexCol>
        <BookingHeader />

        <main className="booking-content pt-8 pb-10 flex flex-col items-center flex-1">
          {!token && renderNoToken()}
          {token && pageState === 'success' && renderSuccess()}
          {token && pageState !== 'success' && renderForm()}
        </main>

        <footer className="border-t border-border">
          <div className="flex items-center px-6 py-4">
            <button
              aria-label="Go back"
              className="w-10 h-10 rounded-full border border-brand text-brand bg-card flex items-center justify-center hover:bg-brand/10 transition-colors cursor-pointer group shadow-sm"
              onClick={() => navigate('/')}
              type="button"
            >
              <ChevronLeft size={20} className="group-hover:scale-110 transition-transform" />
            </button>
          </div>
          <BookingFooter />
        </footer>
    </BookingChrome>
  );
};

export default UnsubscribePage;
